import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import {
    type DecodedTokenPayload,
    extractOrgRolesFromTokenPayload,
    extractPermissionsFromTokenPayload,
    shouldTreatPermissionsAsLoading,
} from './useUserPermissions.helpers';

interface UserPermissions {
    permissions: string[];
    hasPermission: (permission: string) => boolean;
    hasAllPermissions: (permissions: string[]) => boolean;
    hasAnyPermission: (permissions: string[]) => boolean;
    hasRequiredManagementPermissions: boolean;
    canManageUsers: boolean;
    orgRoles: string[];
    isLoading: boolean;
}

export const useUserPermissions = (): UserPermissions => {
    const { user, isLoading: authLoading, getAccessTokenSilently, isAuthenticated } = useAuth0();
    const [tokenPermissions, setTokenPermissions] = useState<string[]>([]);
    const [tokenOrgRoles, setTokenOrgRoles] = useState<string[]>([]);
    const [isLoadingToken, setIsLoadingToken] = useState(true);
    const [hasGracePeriod, setHasGracePeriod] = useState(true);
    const hasLoadedPermissions = useRef(false);
    const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Emergency permissions loader - bypasses all the useEffect logic
    const forceLoadPermissions = useCallback(async () => {
        if (!isAuthenticated) return;

        console.log('🚨 FORCE LOADING PERMISSIONS');
        try {
            const token = await getAccessTokenSilently();
            const payload = token.split('.')[1];
            const decodedPayload = JSON.parse(atob(payload)) as DecodedTokenPayload;

            console.log('🚨 FORCE: Token payload:', decodedPayload);

            if (Array.isArray(decodedPayload.permissions)) {
                console.log('🚨 FORCE: Setting permissions:', decodedPayload.permissions);
                setTokenPermissions(decodedPayload.permissions);
                setIsLoadingToken(false);
                return true;
            }
        } catch (err) {
            console.error('🚨 FORCE: Failed:', err);
        }
        return false;
    }, [isAuthenticated, getAccessTokenSilently]);

    // Fetch and decode the access token to get permissions
    useEffect(() => {
        let mounted = true;

        console.log('useEffect permissions triggered:', {
            isAuthenticated,
            hasLoadedPermissions: hasLoadedPermissions.current,
            tokenPermissionsLength: tokenPermissions.length,
            isLoadingToken,
        });

        const getPermissionsFromToken = async () => {
            console.log('getPermissionsFromToken called', { isAuthenticated, mounted });

            if (!isAuthenticated) {
                console.log('Not authenticated, skipping permission extraction');
                if (mounted) setIsLoadingToken(false);
                return;
            }

            try {
                const token = await getAccessTokenSilently();
                if (!mounted) return; // Component unmounted, don't update state

                // Decode the JWT token (base64 decode the payload)
                const payload = token.split('.')[1];
                const decodedPayload = JSON.parse(atob(payload)) as DecodedTokenPayload;

                console.log('Token payload for permissions:', {
                    scope: decodedPayload.scope,
                    permissions: decodedPayload.permissions,
                    hasScope: !!decodedPayload.scope,
                    hasPermissions: !!decodedPayload.permissions,
                });

                const extractedPermissions = extractPermissionsFromTokenPayload(decodedPayload);

                if (extractedPermissions.length === 0) {
                    console.warn('No permissions found in token payload');
                }

                console.log('FINAL EXTRACTED PERMISSIONS:', extractedPermissions);

                if (mounted && extractedPermissions.length > 0) {
                    setTokenPermissions(extractedPermissions);
                } else if (extractedPermissions.length === 0) {
                    console.error('Failed to extract any permissions from token!');
                }

                // Extract organization roles from the token
                const extractedOrgRoles = extractOrgRolesFromTokenPayload(decodedPayload);
                if (mounted && extractedOrgRoles.length > 0) {
                    setTokenOrgRoles(extractedOrgRoles);
                }
            } catch (error) {
                console.error('Error getting permissions from token:', error);
            } finally {
                if (mounted) {
                    setIsLoadingToken(false);
                    // Clear the timeout since we've finished loading
                    if (loadingTimeoutRef.current) {
                        clearTimeout(loadingTimeoutRef.current);
                        loadingTimeoutRef.current = null;
                    }
                }
            }
        };

        // Only run if we haven't already loaded permissions and we're authenticated
        console.log('useEffect condition check:', {
            hasLoadedPermissions: hasLoadedPermissions.current,
            isAuthenticated,
            shouldLoad: !hasLoadedPermissions.current && isAuthenticated,
        });

        if (!hasLoadedPermissions.current && isAuthenticated) {
            console.log('✅ Running getPermissionsFromToken');
            hasLoadedPermissions.current = true;

            // Set a timeout to prevent infinite loading
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }
            loadingTimeoutRef.current = setTimeout(() => {
                if (mounted) {
                    console.warn('Permissions loading timed out after 10 seconds');
                    setIsLoadingToken(false);
                }
            }, 10000);

            getPermissionsFromToken();
        } else if (!isAuthenticated) {
            console.log('❌ Not authenticated, resetting');
            hasLoadedPermissions.current = false;
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
                loadingTimeoutRef.current = null;
            }
            setIsLoadingToken(false);
        } else if (isAuthenticated && hasLoadedPermissions.current && isLoadingToken) {
            console.log('⚠️ Already loaded but still loading, fixing');
            // Safety check: if we've loaded permissions but still showing as loading, fix it
            setIsLoadingToken(false);
        } else {
            console.log('⏸️ Skipping permission load:', {
                hasLoaded: hasLoadedPermissions.current,
                isAuth: isAuthenticated,
            });
        }

        return () => {
            mounted = false;
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
                loadingTimeoutRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]); // Only depend on authentication status

    // Additional safety check to ensure loading doesn't get stuck
    useEffect(() => {
        if (!authLoading && isAuthenticated !== undefined) {
            // Give it 2 seconds after auth resolves, then force loading to false if still loading
            const fallbackTimeout = setTimeout(() => {
                if (isLoadingToken) {
                    console.warn('Forcing permissions loading to complete as fallback');
                    // Emergency fallback: force load permissions
                    if (tokenPermissions.length === 0 && isAuthenticated) {
                        console.warn('Emergency: Using force loader');
                        forceLoadPermissions();
                    }
                    setIsLoadingToken(false);
                }
            }, 2000);

            return () => clearTimeout(fallbackTimeout);
        }
        // Return undefined for other code paths
        return undefined;
    }, [
        authLoading,
        isAuthenticated,
        isLoadingToken,
        tokenPermissions.length,
        forceLoadPermissions,
    ]);

    // Get permissions from the user object or token
    const permissions = useMemo(() => {
        // Try user object first, then fall back to token permissions
        const userPermissions = user?.permissions || tokenPermissions;
        const finalPermissions = Array.isArray(userPermissions) ? userPermissions : [];

        console.log('Final permissions computed:', {
            userPermissions: user?.permissions,
            tokenPermissions,
            finalPermissions,
            hasReadUsers: finalPermissions.includes('read:users'),
            hasAdminUsers: finalPermissions.includes('administrate:users'),
        });

        return finalPermissions;
    }, [user, tokenPermissions]);

    // Get the organization roles from custom claims
    const orgRoles = useMemo(() => {
        const userOrgRoles = user?.['wevo/org_roles'];
        if (Array.isArray(userOrgRoles)) {
            return userOrgRoles;
        }
        return tokenOrgRoles;
    }, [user, tokenOrgRoles]);

    const hasPermission = useCallback(
        (permission: string): boolean => {
            const result = permissions.includes(permission);
            return result;
        },
        [permissions],
    );

    const hasAllPermissions = useCallback(
        (requiredPermissions: string[]): boolean => {
            return requiredPermissions.every((permission) => permissions.includes(permission));
        },
        [permissions],
    );

    const hasAnyPermission = useCallback(
        (requiredPermissions: string[]): boolean => {
            return requiredPermissions.some((permission) => permissions.includes(permission));
        },
        [permissions],
    );

    // Check if user has the required management permissions
    // Only users with administrate:users permission should see admin-only information
    const hasRequiredManagementPermissions = useMemo(() => {
        return hasPermission('administrate:users');
    }, [hasPermission]);

    // Check if user can manage users/groups
    // Users with manage:users permission can perform management actions
    const canManageUsers = useMemo(() => {
        return hasPermission('manage:users');
    }, [hasPermission]);

    // EMERGENCY FIX: If we're not loading and have no permissions but user is authenticated, force load immediately
    useEffect(() => {
        if (!authLoading && !isLoadingToken && isAuthenticated && permissions.length === 0) {
            console.log('🚨 EMERGENCY: No permissions detected, force loading immediately');
            forceLoadPermissions();
        }
    }, [authLoading, isLoadingToken, isAuthenticated, permissions.length, forceLoadPermissions]);

    // Grace period: give 1 second after auth completes before allowing access denied
    useEffect(() => {
        if (!authLoading && isAuthenticated) {
            const graceTimer = setTimeout(() => {
                setHasGracePeriod(false);
            }, 1000);
            return () => clearTimeout(graceTimer);
        } else if (!isAuthenticated) {
            setHasGracePeriod(true);
        }
        // Return undefined for other code paths
        return undefined;
    }, [authLoading, isAuthenticated]);

    // Conservative loading logic: if authenticated but no permissions, still consider loading
    const isActuallyLoading = shouldTreatPermissionsAsLoading({
        authLoading,
        isLoadingToken,
        isAuthenticated,
        permissionCount: permissions.length,
        hasGracePeriod,
    });

    return {
        permissions,
        hasPermission,
        hasAllPermissions,
        hasAnyPermission,
        hasRequiredManagementPermissions,
        canManageUsers,
        orgRoles,
        isLoading: isActuallyLoading,
    };
};
